/* eslint no-console:0 */
import { newSpecPage } from "@stencil/core/testing";
import { Dialog } from "../dialog";

declare const global;

describe("uic-dialog", () => {
    global.MutationObserver = class {
        constructor() {}
        disconnect() {}
        observe() {}
        takeRecords(): MutationRecord[] {
            return [];
        }
    };

    it("renders after calling open", async () => {
        const page = await newSpecPage({
            components: [Dialog],
            html: `<uic-dialog><p>Sample content</p></uic-dialog>`
        });
        expect(page.rootInstance).toHaveProperty("isOpen", false);
        expect(page.root).toEqualAttribute("style", "display: none;");
        // mock focus, see https://github.com/ionic-team/stencil/issues/1964
        (page.root.getElementsByClassName("uic-dialog__panel")[0] as HTMLElement).focus = jest.fn();

        page.root.open();
        await page.waitForChanges();
        expect(page.rootInstance).toHaveProperty("isOpen", true);
        await page.waitForChanges();
        expect(page.root).toEqualAttribute("style", "display: block;");
        expect(page.root.textContent).toBe("Sample content");
    });

    it("renders with attribute is-open", async () => {
        const page = await newSpecPage({
            components: [Dialog],
            html: `<uic-dialog is-open><p>Sample content</p></uic-dialog>`
        });
        // mock focus, see https://github.com/ionic-team/stencil/issues/1964
        (page.root.getElementsByClassName("uic-dialog__panel")[0] as HTMLElement).focus = jest.fn();
        expect(page.rootInstance).toHaveProperty("isOpen", true);
        await page.waitForChanges();
        expect(page.root).toEqualAttribute("style", "display: block;");
        expect(page.root.textContent).toBe("Sample content");
    });

    it("renders the title", async () => {
        const page = await newSpecPage({
            components: [Dialog],
            html: `<uic-dialog is-open dialog-title="Test title"><p>Sample content</p></uic-dialog>`
        });
        // mock focus, see https://github.com/ionic-team/stencil/issues/1964
        (page.root.getElementsByClassName("uic-dialog__panel")[0] as HTMLElement).focus = jest.fn();
        await page.waitForChanges();
        expect(page.root.querySelector(".uic-dialog__panel-header").textContent).toBe("Test title");
    });

    it("renders the close button if dismissable", async () => {
        const page = await newSpecPage({
            components: [Dialog],
            html: `<uic-dialog is-open dialog-title="Test title"><p>Sample content</p></uic-dialog>`
        });
        // mock focus, see https://github.com/ionic-team/stencil/issues/1964
        (page.root.getElementsByClassName("uic-dialog__panel")[0] as HTMLElement).focus = jest.fn();
        await page.waitForChanges();
        expect(page.root.querySelector(".uic-dialog__panel-header uic-icon-button")).toBeTruthy();

        page.root.setAttribute("dismissable", "false");
        await page.waitForChanges();
        expect(page.root.querySelector(".uic-dialog__panel-header uic-icon-button")).toBeFalsy();
    });

    it("renders the icon", async () => {
        const page = await newSpecPage({
            components: [Dialog],
            html: `<uic-dialog is-open dialog-title="Test title"><p>Sample content</p></uic-dialog>`
        });
        // mock focus, see https://github.com/ionic-team/stencil/issues/1964
        (page.root.getElementsByClassName("uic-dialog__panel")[0] as HTMLElement).focus = jest.fn();
        await page.waitForChanges();
        expect(page.root.querySelector(".uic-dialog__panel-header-icon")).toBeFalsy();

        page.root.type = "warning";
        await page.waitForChanges();
        expect(page.root.querySelector(".uic-dialog__panel-header-icon")).toBeTruthy();

        page.root.type = "error";
        await page.waitForChanges();
        expect(page.root.querySelector(".uic-dialog__panel-header-icon")).toBeTruthy();

        page.root.type = "default";
        page.root.setAttribute("icon", "diagnose");
        await page.waitForChanges();
        expect(page.root.querySelector(".uic-dialog__panel-header-icon")).toBeTruthy();
    });

    it("hides the header", async () => {
        const page = await newSpecPage({
            components: [Dialog],
            html: `<uic-dialog is-open><p>Sample content</p></uic-dialog>`
        });
        // mock focus, see https://github.com/ionic-team/stencil/issues/1964
        (page.root.getElementsByClassName("uic-dialog__panel")[0] as HTMLElement).focus = jest.fn();
        await page.waitForChanges();
        expect(page.root.querySelector(".uic-dialog__panel-header")).toBeTruthy();

        page.root.dismissable = false;
        await page.waitForChanges();
        expect(page.root.querySelector(".uic-dialog__panel-header")).toHaveClass("uic-hidden");

        page.root.dialogTitle = "Test title";
        await page.waitForChanges();
        expect(page.root.querySelector(".uic-dialog__panel-header")).not.toHaveClass("uic-hidden");

        page.root.dialogTitle = null;
        page.root.type = "warning";
        await page.waitForChanges();
        expect(page.root.querySelector(".uic-dialog__panel-header")).not.toHaveClass("uic-hidden");

        page.root.dialogTitle = null;
        page.root.type = "default";
        page.root.icon = "diagnose";
        await page.waitForChanges();
        expect(page.root.querySelector(".uic-dialog__panel-header")).not.toHaveClass("uic-hidden");
    });

    it("hides the footer without buttons", async () => {
        const page = await newSpecPage({
            components: [Dialog],
            html: `<uic-dialog is-open dialog-title="Test title"><p>Sample content</p></uic-dialog>`
        });
        // mock focus, see https://github.com/ionic-team/stencil/issues/1964
        (page.root.getElementsByClassName("uic-dialog__panel")[0] as HTMLElement).focus = jest.fn();
        await page.waitForChanges();
        expect(page.root.querySelector("uic-footer")).toHaveClass("uic-hidden");
    });

    it("shows the footer buttons", async () => {
        const page = await newSpecPage({
            components: [Dialog],
            html: `<uic-dialog is-open dialog-title="Test title">
                        <p>Sample content</p>
                        <uic-button
                            slot="footer-button"
                            uic-close-dialog-button
                            .onclick=${() => console.log("custom action before close")}
                        >Close</uic-button>
                    </uic-dialog>`
        });
        // mock focus, see https://github.com/ionic-team/stencil/issues/1964
        (page.root.getElementsByClassName("uic-dialog__panel")[0] as HTMLElement).focus = jest.fn();
        await page.waitForChanges();
        expect(page.root.querySelector("uic-footer")).not.toHaveClass("uic-hidden");
    });
});
